import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { Link as RouterLink } from "react-router-dom"
import { type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"
import { linkVariants } from "./link.variants"

export interface LinkProps
  extends React.ComponentProps<"a">,
  VariantProps<typeof linkVariants> {
  /**
   * If true, the link will render as its child element (useful for router links)
   */
  asChild?: boolean
  /**
   * Icon to display before the link text
   */
  leadingIcon?: React.ReactNode
  /**
   * Icon to display after the link text
   */
  trailingIcon?: React.ReactNode
  /**
   * If true, the link will take up the full width of its container
   */
  fullWidth?: boolean
  /**
   * If true, the link will be visually and functionally disabled
   */
  disabled?: boolean
}

function Link({
  className,
  variant = "default",
  size = "md",
  asChild = false,
  leadingIcon,
  trailingIcon,
  fullWidth = false,
  disabled,
  target,
  rel,
  children,
  href,
  ...props
}: LinkProps) {
  // Determine if link is external
  const isExternal = target === "_blank" || (href && /^https?:\/\//.test(href)) || (href && href.startsWith('mailto:')) || (href && href.startsWith('tel:'))
  const isInternal = href && (href.startsWith('/') || href.startsWith('#')) && !isExternal

  // Add security attributes for external links
  const secureRel = isExternal
    ? rel
      ? `${rel} noopener noreferrer`
      : "noopener noreferrer"
    : rel

  // Auto-set target for external URLs if not specified
  const resolvedTarget = isExternal && !target ? "_blank" : target

  const commonProps = {
    "data-slot": "link",
    "data-variant": variant,
    "data-size": size,
    className: cn(
      linkVariants({ variant, size, className }),
      fullWidth && "w-full",
      disabled && "pointer-events-none opacity-50"
    ),
    "aria-disabled": disabled,
    tabIndex: disabled ? -1 : undefined,
    ...props
  }

  if (asChild) {
    return (
      <Slot {...commonProps}>
        {children}
      </Slot>
    )
  }

  if (isInternal) {
    return (
      <RouterLink
        to={href!}
        {...commonProps}
      >
        {leadingIcon}
        {children}
        {trailingIcon}
      </RouterLink>
    )
  }

  return (
    <a
      href={disabled ? undefined : href}
      target={resolvedTarget}
      rel={secureRel}
      {...commonProps}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </a>
  )
}

Link.displayName = 'Link'

export default Link
